import React, {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from "react";
import { Search, Trash2, X } from "lucide-react";
import { TailSpin } from "react-loader-spinner";

import { Select } from "../../../../ui/select/select.tsx";
import { showError, showSuccess } from "../../../../../utils/toast.ts";
import { useEmployeeTechStack } from "../hooks/use-employee-tech-stack.ts";
import type {
  EmployeeTechStack as EmployeeTechStackItem,
  SkillLevel,
  TechStackCategory,
  TechStackOption,
} from "../types.ts";

import styles from "../employee-tech-stack.module.css";

type EmployeeTechStackProps = {
  employeeId?: number;
  accessToken?: string;
};

type VisibleTechStack = TechStackOption & {
  category: TechStackCategory;
};

const PROFICIENCY_LEVELS = [
  { value: "BEGINNER", label: "Beginner" },
  { value: "INTERMEDIATE", label: "Intermediate" },
  { value: "EXPERIENCED", label: "Experienced" },
];

function normalizeSelectedTechStacks(
  employeeTechStacks: EmployeeTechStackItem[] | undefined,
): EmployeeTechStackItem[] {
  return (employeeTechStacks ?? []).map((skill) => ({
    techStackId: Number(skill.techStackId),
    category: skill.category ?? null,
    techStack: skill.techStack ?? "",
    skillLevel: skill.skillLevel ?? "BEGINNER",
    experience: skill.experience ?? null,
    primarySkill: Boolean(skill.primarySkill),
    logo: skill.logo ?? null,
  }));
}

function findCategoryForSkill(
  categories: TechStackCategory[],
  techStackId: number,
): TechStackCategory | undefined {
  return categories.find((category) =>
    category.techStacks.some((skill) => skill.techStackId === techStackId),
  );
}

function CategoryMark({ category }: { category: TechStackCategory }) {
  const categoryStyle = {
    "--category-color": category.colorCode || "#2563eb",
  } as CSSProperties;

  return (
    <span
      className={styles.categoryMark}
      style={categoryStyle}
      aria-hidden
    >
      <span>{category.category.slice(0, 1).toUpperCase()}</span>
      {category.logo ? (
        <img
          className={styles.categoryLogo}
          src={category.logo.trim()}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      ) : null}
    </span>
  );
}

function TechStackMark({
  skill,
  category,
}: {
  skill: Pick<TechStackOption, "techStack" | "logo">;
  category: TechStackCategory;
}) {
  const logo = skill.logo?.trim() || category.logo?.trim();
  const markStyle = {
    "--category-color": category.colorCode || "#2563eb",
  } as CSSProperties;

  return (
    <span className={styles.categoryMark} style={markStyle} aria-hidden>
      <span>{skill.techStack.slice(0, 1).toUpperCase()}</span>
      {logo ? (
        <img
          className={styles.categoryLogo}
          src={logo}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      ) : null}
    </span>
  );
}

export function EmployeeTechStack({
  employeeId,
  accessToken,
}: EmployeeTechStackProps) {
  const {
    categories,
    employeeTechStack,
    loading,
    saving,
    error,
    save,
    remove,
  } = useEmployeeTechStack(accessToken, employeeId);
  const [selectedSkills, setSelectedSkills] = useState<
    EmployeeTechStackItem[]
  >([]);
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(
    null,
  );
  const [search, setSearch] = useState("");
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());
  const [persistedSkillIds, setPersistedSkillIds] = useState<Set<number>>(
    new Set(),
  );

  useEffect(() => {
    if (activeCategoryId == null && categories.length > 0) {
      setActiveCategoryId(categories[0].categoryId);
    }
  }, [activeCategoryId, categories]);

  useEffect(() => {
    const persistedSkills = normalizeSelectedTechStacks(
      employeeTechStack?.techStacks,
    );
    setSelectedSkills(persistedSkills);
    setPersistedSkillIds(
      new Set(persistedSkills.map((skill) => skill.techStackId)),
    );
  }, [employeeTechStack]);

  useEffect(() => {
    if (error) showError(error);
  }, [error]);

  const selectedIds = useMemo(
    () => new Set(selectedSkills.map((skill) => skill.techStackId)),
    [selectedSkills],
  );

  const visibleSkills = useMemo<VisibleTechStack[]>(() => {
    const query = search.trim().toLowerCase();
    if (query) {
      return categories.flatMap((category) =>
        category.techStacks
          .filter((skill) => skill.techStack.toLowerCase().includes(query))
          .map((skill) => ({ ...skill, category })),
      );
    }

    const activeCategory = categories.find(
      (category) => category.categoryId === activeCategoryId,
    );
    if (!activeCategory) return [];

    return activeCategory.techStacks.map((skill) => ({
      ...skill,
      category: activeCategory,
    }));
  }, [activeCategoryId, categories, search]);

  const toggleSkill = (
    skill: TechStackOption,
    category: TechStackCategory,
  ) => {
    setSelectedSkills((current) => {
      const exists = current.some(
        (item) => item.techStackId === skill.techStackId,
      );
      if (exists) {
        return current.filter(
          (item) => item.techStackId !== skill.techStackId,
        );
      }

      return [
        ...current,
        {
          techStackId: skill.techStackId,
          category: category.category,
          techStack: skill.techStack,
          skillLevel: skill.skillLevel ?? "BEGINNER",
          experience: null,
          primarySkill: current.length === 0,
          logo: skill.logo ?? null,
        },
      ];
    });
  };

  const removeSkillLocally = (techStackId: number) => {
    setSelectedSkills((current) => {
      const removedPrimary = current.find(
        (skill) => skill.techStackId === techStackId,
      )?.primarySkill;
      const next = current.filter(
        (skill) => skill.techStackId !== techStackId,
      );
      if (removedPrimary && next.length > 0) {
        next[0] = { ...next[0], primarySkill: true };
      }
      return next;
    });
  };

  const deleteSkill = async (techStackId: number) => {
    if (!persistedSkillIds.has(techStackId)) {
      removeSkillLocally(techStackId);
      return;
    }

    setDeletingIds((current) => new Set(current).add(techStackId));
    try {
      await remove(techStackId);
      removeSkillLocally(techStackId);
      setPersistedSkillIds((current) => {
        const next = new Set(current);
        next.delete(techStackId);
        return next;
      });
      showSuccess("Technology removed successfully.");
    } catch {
      showError("Unable to remove technology.");
    } finally {
      setDeletingIds((current) => {
        const next = new Set(current);
        next.delete(techStackId);
        return next;
      });
    }
  };

  const clearAll = () => setSelectedSkills([]);

  const updateSkillLevel = (techStackId: number, skillLevel: string) => {
    setSelectedSkills((current) =>
      current.map((skill) =>
        skill.techStackId === techStackId
          ? { ...skill, skillLevel: skillLevel as SkillLevel }
          : skill,
      ),
    );
  };

  const handleSave = async () => {
    if (selectedSkills.length === 0) return;

    try {
      await save({
        techStacks: selectedSkills.map(({ techStackId, skillLevel }) => ({
          techStackId,
          skillLevel: skillLevel ?? "BEGINNER",
          primarySkill: false,
        })),
      });
      setPersistedSkillIds(
        new Set(selectedSkills.map((skill) => skill.techStackId)),
      );
      showSuccess("Technical stack updated successfully.");
    } catch {
      showError("Unable to update technical stack.");
    }
  };

  if (loading) {
    return (
      <div className={styles.loader}>
        <TailSpin
          height="42"
          width="42"
          color="#0d6efd"
          ariaLabel="Loading technical stack"
        />
      </div>
    );
  }

  return (
    <section className={styles.root} aria-label="Technical Stack">
  

      <div className={styles.selectorHeader}>
        <label className={styles.searchBox}>
          <Search size={18} aria-hidden />
          <span className="visually-hidden">Search technologies</span>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search and select Technologies..."
          />
        </label>

        <div className={styles.selectedSummary}>
          <span className={styles.summaryLabel}>
            Selected Technologies ({selectedSkills.length})
          </span>
          <div className={styles.chips}>
            {selectedSkills.map((skill) => (
              <span className={styles.chip} key={skill.techStackId}>
                {skill.techStack}
                <button
                  type="button"
                  onClick={() => removeSkillLocally(skill.techStackId)}
                  disabled={saving}
                  aria-label={`Remove ${skill.techStack}`}
                >
                  <X size={14} aria-hidden />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.picker}>
        <div className={styles.categories} role="tablist" aria-label="Technology categories">
          {categories.map((category) => (
            <button
              type="button"
              role="tab"
              aria-selected={activeCategoryId === category.categoryId}
              className={
                activeCategoryId === category.categoryId && !search
                  ? styles.categoryActive
                  : styles.category
              }
              key={category.categoryId}
              onClick={() => {
                setSearch("");
                setActiveCategoryId(category.categoryId);
              }}
            >
              <CategoryMark category={category} />
              {category.category}
            </button>
          ))}
        </div>

        <div className={styles.technologyPanel}>
          <div className={styles.skillsGrid}>
            {visibleSkills.length > 0 ? (
              visibleSkills.map((skill) => {
                const category = skill.category ??
                  findCategoryForSkill(categories, skill.techStackId);
                if (!category) return null;
                return (
                  <label className={styles.skillOption} key={skill.techStackId}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(skill.techStackId)}
                      onChange={() => toggleSkill(skill, category)}
                    />
                    <TechStackMark skill={skill} category={category} />
                    <span>{skill.techStack}</span>
                  </label>
                );
              })
            ) : (
              <p className={styles.empty}>No technologies found.</p>
            )}
          </div>

          <section
            className={styles.proficiencyTable}
            aria-labelledby="selected-proficiency-heading"
          >
            <h3
              id="selected-proficiency-heading"
              className={styles.proficiencyHeading}
            >
              Selected Technologies &amp; Proficiency Level
            </h3>
            {selectedSkills.length > 0 ? (
              <div className={styles.proficiencyRows}>
                {selectedSkills.map((skill, index) => {
                  const category = findCategoryForSkill(
                    categories,
                    skill.techStackId,
                  );
                  return (
                    <div className={styles.proficiencyRow} key={skill.techStackId}>
                      <div className={styles.proficiencyTechnology}>
                        {category ? (
                          <TechStackMark skill={skill} category={category} />
                        ) : (
                          <span className={styles.fallbackMark} aria-hidden>
                            {skill.techStack.slice(0, 1).toUpperCase()}
                          </span>
                        )}
                        <span>{skill.techStack}</span>
                      </div>
                      <span className={styles.levelLabel}>Level</span>
                      <div className={styles.levelSelect}>
                        <Select
                          id={`profile-tech-stack-level-${index}`}
                          value={skill.skillLevel ?? "BEGINNER"}
                          options={PROFICIENCY_LEVELS}
                          onChange={(value) =>
                            updateSkillLevel(skill.techStackId, value)
                          }
                          density="compact"
                        />
                      </div>
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => deleteSkill(skill.techStackId)}
                        disabled={deletingIds.has(skill.techStackId)}
                        aria-label={`Remove ${skill.techStack}`}
                      >
                        <Trash2 size={18} aria-hidden />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className={styles.proficiencyEmpty}>
                Select technologies above to set proficiency levels.
              </p>
            )}
          </section>
        </div>
      </div>

      <div className={styles.footer}>
        <span>{selectedSkills.length} selected</span>
        <div>
          <button
            type="button"
            className={styles.clearButton}
            onClick={clearAll}
            disabled={saving || selectedSkills.length === 0}
          >
            Clear All
          </button>
          <button
            type="button"
            className={styles.saveButton}
            onClick={handleSave}
            disabled={saving || selectedSkills.length === 0}
          >
            {saving ? "Saving..." : "Done"}
          </button>
        </div>
      </div>
    </section>
  );
}
